import { DialOptionType } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-path/v1/api.pb';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import { PieChartData } from '@frontend/charts';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { AggregatedPhoneTreeCallVolumeType, PhoneTreeAnalyticsIOType } from '../components/charts/shared-types';
import { DialOption, DialOptionNumber } from '../overview-cards/phone-tree-call-volume';

export type TransformedChartData = Partial<PhoneTreeAnalyticsIOType> & {
  groups: PieChartData['groups'];
  phoneNumbers: { locationName: string; number: string }[] | undefined;
};

const dialOptionSortOrder: Record<DialOptionType, number> = {
  [DialOptionType.DIAL_OPTION_NUMBER]: 0,
  [DialOptionType.DIAL_OPTION_EXTENSION]: 1,
  [DialOptionType.DIAL_OPTION_FALLBACK]: 2,
  [DialOptionType.DIAL_OPTION_ABANDONED]: 3,
  [DialOptionType.DIAL_OPTION_UNKNOWN]: 4,
};

export const sortedGroup = (item: PhoneTreeAnalyticsIOType): PieChartData['groups'] =>
  item.dialOptions
    .slice()
    .sort((a, b) => {
      const aOrder = (dialOptionSortOrder[a.type as DialOptionType] as number) ?? 99;
      const bOrder = (dialOptionSortOrder[b.type as DialOptionType] as number) ?? 99;
      return aOrder - bOrder;
    })
    .map((d) => {
      let name: DialOption = 'unknown';
      switch (d.type) {
        case DialOptionType.DIAL_OPTION_NUMBER:
          name = `dialOption${d.dialOptionNumber}` as DialOptionNumber;
          break;
        case DialOptionType.DIAL_OPTION_EXTENSION:
          name = 'extension';
          break;
        case DialOptionType.DIAL_OPTION_FALLBACK:
          name = 'fallback';
          break;
        case DialOptionType.DIAL_OPTION_ABANDONED:
          name = 'abandoned';
          break;
        default:
          name = 'unknown';
      }
      return {
        name,
        value: d.totalCalls,
      };
    });

export const transformChartData = ({
  data,
  allPhoneNumbers,
  locationNames,
}: {
  data?: AggregatedPhoneTreeCallVolumeType[];
  allPhoneNumbers: PhoneNumbersV1.Types.ListPhoneNumbersForLocationsIO['output']['phoneNumbers'];
  locationNames: Record<string, string>;
}): TransformedChartData[] => {
  if (!data) return [];

  const transformed = data.map<TransformedChartData>((item) => {
    const hasDialOptions = 'dialOptions' in item;

    return hasDialOptions
      ? {
          ...item,
          phoneNumbers:
            allPhoneNumbers
              ?.filter((number) => item.officePhoneNumberIds?.includes(number.id))
              ?.map((n) => {
                return {
                  locationName: locationNames[n.locationId],
                  number: formatPhoneNumber(String(n.phoneNumber.nationalNumber) ?? ''),
                };
              }) ?? [],
          groups: sortedGroup(item),
        }
      : {
          ...item,
          groups: [],
          phoneNumbers: [],
          dialOptions: undefined,
        };
  });

  return transformed.sort((a, b) => {
    const aHasDialOptions = !!a.totalCalls;
    const bHasDialOptions = !!b.totalCalls;

    if (!!aHasDialOptions && !!bHasDialOptions) {
      return (b.totalCalls ?? 0) - (a.totalCalls ?? 0);
    }

    if (aHasDialOptions && !bHasDialOptions) return -1;
    if (!aHasDialOptions && bHasDialOptions) return 1;
    return 0;
  });
};
